import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, Users, Mail, Send, Trash2, Crown, Shield, User as UserIcon,
  Copy, Check, Loader2, Plus, Save, AlertTriangle, MoreVertical, X,
  Link as LinkIcon, Eye, Lock, Unlock, Clock, Calendar, FileSpreadsheet, LayoutDashboard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AddSeatsCard from '../components/AddSeatsCard';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  owner: { icon: Crown, label: 'Owner', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  admin: { icon: Shield, label: 'Admin', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-blue-300 border-emerald-500/30' },
  member: { icon: UserIcon, label: 'Member', cls: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30' },
};

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.member;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
};

const OrganizationPage = () => {
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('members');

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');

  // Settings tab
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Member action confirm state
  const [confirmRemove, setConfirmRemove] = useState(null);

  // Copy state
  const [copiedToken, setCopiedToken] = useState('');

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = useCallback(async () => {
    try {
      const [orgRes, mRes, iRes, sRes] = await Promise.all([
        axios.get(`${API}/me/org`, { headers }),
        axios.get(`${API}/org/members`, { headers }),
        axios.get(`${API}/org/invitations`, { headers }).catch(() => ({ data: { invitations: [] } })),
        axios.get(`${API}/share`, { headers }).catch(() => ({ data: { links: [] } })),
      ]);
      setOrg(orgRes.data);
      setMembers(mRes.data.members || []);
      setInvitations(iRes.data.invitations || []);
      setShareLinks(sRes.data.links || []);
      setEditingName(orgRes.data.organization_name || '');
    } catch (err) {
      console.error('Org refresh failed', err);
      toast.error(err.response?.data?.detail || 'Could not load organization');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const myRole = org?.role || 'member';
  const isOwner = myRole === 'owner';
  const isAdminPlus = myRole === 'owner' || myRole === 'admin';

  // ── Invite handlers ─────────────────────────────────────────────────────────
  const submitInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await axios.post(
        `${API}/org/invite`,
        { email: inviteEmail.trim(), role: inviteRole },
        { headers }
      );
      const link = res.data.invitation?.invite_link || '';
      setLastInviteLink(link);
      toast.success(res.data.message || 'Invitation created');
      setInviteEmail('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally {
      setInviting(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedToken(''), 2500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const revokeInvitation = async (invId) => {
    try {
      await axios.delete(`${API}/org/invitations/${invId}`, { headers });
      toast.success('Invitation revoked');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const removeMember = async () => {
    if (!confirmRemove) return;
    try {
      await axios.delete(`${API}/org/members/${confirmRemove.user_id}`, { headers });
      toast.success(`${confirmRemove.email} removed`);
      setConfirmRemove(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
      setConfirmRemove(null);
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.patch(
        `${API}/org/members/${userId}/role`,
        { role: newRole },
        { headers }
      );
      toast.success(`Role updated to ${newRole}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change role');
    }
  };

  const revokeShareLink = async (linkId) => {
    if (!window.confirm('Revoke this share link? Anyone using it will lose access immediately.')) return;
    try {
      await axios.delete(`${API}/share/${linkId}`, { headers });
      toast.success('Share link revoked');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const saveName = async () => {
    if (!editingName.trim() || editingName === org?.organization_name) return;
    setSavingName(true);
    try {
      await axios.patch(`${API}/org/settings`, { name: editingName.trim() }, { headers });
      toast.success('Organization name updated');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update name');
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-slate-200 dark:bg-slate-800 rounded-2xl w-1/3" />
          <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="organization-page">
      {/* Header */}
      <div className="rounded-2xl border border-emerald-500/15 bg-gradient-to-br from-emerald-500/[0.06] via-cyan-500/[0.04] to-transparent p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest">
                ORGANIZATION
              </Badge>
              {org?.is_personal && (
                <Badge variant="outline" className="text-[11px] font-bold tracking-widest">
                  PERSONAL
                </Badge>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white truncate">
              {org?.organization_name || 'Organization'}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-400 dark:text-slate-400">
              <span className="font-mono text-xs">{org?.organization_slug}</span>
              <span>·</span>
              <span>{members.length} {members.length === 1 ? 'member' : 'members'}</span>
              <span>·</span>
              <span>{invitations.length} pending {invitations.length === 1 ? 'invite' : 'invites'}</span>
              <span>·</span>
              <span className="capitalize">{org?.plan || 'free'} plan</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge role={myRole} />
            {isAdminPlus && (
              <Button onClick={() => setInviteOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invite-member-btn">
                <Plus className="w-4 h-4 mr-1.5" />
                Invite Member
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl" data-testid="org-tabs">
          <TabsTrigger value="members" data-testid="tab-members">
            <Users className="w-4 h-4 mr-1.5" />
            Members
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">
            <Mail className="w-4 h-4 mr-1.5" />
            Invites
            {invitations.length > 0 && (
              <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">{invitations.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="share-links" data-testid="tab-share-links">
            <LinkIcon className="w-4 h-4 mr-1.5" />
            Share Links
            {shareLinks.length > 0 && (
              <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-cyan-300">{shareLinks.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Building2 className="w-4 h-4 mr-1.5" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Members tab */}
        <TabsContent value="members" className="mt-4 space-y-4" data-testid="members-tab-content">
          {/* Add seats / upgrade card sits above the member list */}
          <AddSeatsCard
            currentSeatLimit={org?.seat_limit}
            currentSeatCount={members.length}
            plan={org?.plan}
          />
          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>People with access to {org?.organization_name}.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {members.map((m, idx) => (
                  <div
                    key={m.user_id}
                    className="flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900/30"
                    data-testid={`member-row-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {(m.name || m.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-white truncate">
                          {m.name || m.email}
                          {m.is_self && <span className="ml-2 text-xs font-normal text-slate-400">(you)</span>}
                        </div>
                        <div className="text-xs text-slate-400 truncate">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isOwner && !m.is_self ? (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`role-select-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <RoleBadge role={m.role} />
                      )}
                      {isAdminPlus && !m.is_self && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 hover:text-rose-700"
                          onClick={() => setConfirmRemove(m)}
                          data-testid={`remove-member-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations tab */}
        <TabsContent value="invitations" className="mt-4" data-testid="invitations-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>People who've been invited but haven't accepted yet. Invitations expire in 7 days.</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <div className="text-center py-10 text-slate-400 dark:text-slate-400">
                  <Mail className="w-8 h-8 mx-auto mb-3 opacity-60" />
                  <p className="text-sm">No pending invitations.</p>
                  {isAdminPlus && (
                    <Button onClick={() => setInviteOpen(true)} variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-1.5" />
                      Invite your first member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {invitations.map((inv, idx) => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900/30"
                      data-testid={`invitation-row-${idx}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center shrink-0">
                          <Mail className="w-4 h-4 text-amber-600 dark:text-white" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 dark:text-white truncate">{inv.email}</div>
                          <div className="text-xs text-slate-400 truncate">
                            <RoleBadge role={inv.role} />
                            <span className="ml-2">expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(inv.invite_link, inv.id)}
                          data-testid={`copy-invite-${idx}`}
                        >
                          {copiedToken === inv.id ? (
                            <><Check className="w-4 h-4 mr-1 text-emerald-500" /> Copied</>
                          ) : (
                            <><Copy className="w-4 h-4 mr-1" /> Copy link</>
                          )}
                        </Button>
                        {isAdminPlus && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => revokeInvitation(inv.id)}
                            data-testid={`revoke-invite-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Share Links tab */}
        <TabsContent value="share-links" className="mt-4" data-testid="share-links-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-emerald-500" />
                    Public Share Links
                  </CardTitle>
                  <CardDescription>
                    Read-only signed links for sharing dashboards or FOCUS exports outside your organization.
                    {myRole === 'member' ? ' Showing your links.' : ' Showing all org links.'}
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs">{shareLinks.length} active</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {shareLinks.length === 0 ? (
                <div className="text-center py-10 text-slate-400 dark:text-slate-400">
                  <LinkIcon className="w-8 h-8 mx-auto mb-3 opacity-60" />
                  <p className="text-sm font-medium mb-2">No share links yet</p>
                  <p className="text-xs max-w-sm mx-auto leading-relaxed">
                    Create one from any dashboard or FOCUS export — they're signed, scoped to a single artifact, and expire automatically.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {shareLinks.map((link, idx) => {
                    const Icon = link.artifact_type === 'dashboard' ? LayoutDashboard : FileSpreadsheet;
                    const expiresDate = link.expires_at ? new Date(link.expires_at) : null;
                    const isExpired = expiresDate && expiresDate < new Date();
                    const isRevoked = link.revoked;
                    return (
                      <div
                        key={link.id}
                        className={`flex items-center justify-between gap-3 p-4 rounded-xl border ${
                          isRevoked || isExpired
                            ? 'border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/20 opacity-60'
                            : 'border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900/30'
                        }`}
                        data-testid={`sharelink-row-${idx}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-emerald-600 dark:text-cyan-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-semibold text-slate-900 dark:text-white truncate">
                                {link.label || (link.artifact_type === 'dashboard' ? 'Shared Dashboard' : 'FOCUS Export')}
                              </span>
                              {link.has_password && (
                                <Lock className="w-3 h-3 text-amber-500 shrink-0" />
                              )}
                              {isRevoked && (
                                <Badge variant="outline" className="text-[11px] border-rose-500/30 text-rose-600 dark:text-rose-400">REVOKED</Badge>
                              )}
                              {!isRevoked && isExpired && (
                                <Badge variant="outline" className="text-[11px] border-slate-400/30 text-slate-400">EXPIRED</Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{link.view_count || 0}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {expiresDate ? expiresDate.toLocaleDateString() : 'no expiry'}
                              </span>
                              <span className="font-mono truncate text-[11px]">{link.id}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(link.share_url, link.id)}
                            disabled={isRevoked || isExpired}
                            data-testid={`copy-sharelink-${idx}`}
                          >
                            {copiedToken === link.id ? (
                              <><Check className="w-4 h-4 mr-1 text-emerald-500" /> Copied</>
                            ) : (
                              <><Copy className="w-4 h-4 mr-1" /> Copy link</>
                            )}
                          </Button>
                          {!isRevoked && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => revokeShareLink(link.id)}
                              className="text-rose-600 hover:text-rose-700"
                              data-testid={`revoke-sharelink-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings tab */}
        <TabsContent value="settings" className="mt-4" data-testid="settings-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>General settings for your organization.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <div className="flex gap-2">
                  <Input
                    id="org-name"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    disabled={!isAdminPlus || org?.is_personal}
                    maxLength={64}
                    data-testid="org-name-input"
                  />
                  <Button
                    onClick={saveName}
                    disabled={savingName || !isAdminPlus || org?.is_personal || editingName === org?.organization_name}
                    data-testid="save-org-name"
                  >
                    {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-1.5">Save</span>
                  </Button>
                </div>
                {org?.is_personal && (
                  <p className="text-xs text-slate-400">
                    Personal organizations have a fixed name. Create a team organization to customize.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                <div>
                  <Label className="text-xs text-slate-400">Slug</Label>
                  <p className="text-sm font-mono mt-1">{org?.organization_slug}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Plan</Label>
                  <p className="text-sm capitalize mt-1">{org?.plan || 'free'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Seat limit</Label>
                  <p className="text-sm mt-1">{org?.seat_limit ?? 'Unlimited'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Invite dialog ──────────────────────────────────────────────────── */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setLastInviteLink(''); }}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" />
              Invite a Member
            </DialogTitle>
            <DialogDescription>
              They'll receive an email with a secure invite link that expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                data-testid="invite-email-input"
                disabled={inviting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole} disabled={inviting}>
                <SelectTrigger id="invite-role" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isOwner && <SelectItem value="owner">Owner — full control including billing</SelectItem>}
                  <SelectItem value="admin">Admin — manage members + integrations</SelectItem>
                  <SelectItem value="member">Member — read & use features</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {lastInviteLink && (
              <div className="space-y-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-xs font-bold tracking-widest text-emerald-700 dark:text-emerald-300">SHARE LINK</div>
                <div className="flex gap-2 items-center">
                  <Input
                    readOnly
                    value={lastInviteLink}
                    className="text-xs font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(lastInviteLink, 'last')}
                    data-testid="copy-last-invite"
                  >
                    {copiedToken === 'last' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">Email sent — share this link directly if needed.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviting}>Close</Button>
            <Button
              onClick={submitInvite}
              disabled={inviting || !inviteEmail.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-invite-btn"
            >
              {inviting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Sending…</> : <><Send className="w-4 h-4 mr-1.5" />Send Invite</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Confirm remove member dialog ─────────────────────────────────── */}
      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-remove-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Remove Member
            </DialogTitle>
            <DialogDescription>
              Remove <strong>{confirmRemove?.name || confirmRemove?.email}</strong> from{' '}
              <strong>{org?.organization_name}</strong>? They'll lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button onClick={removeMember} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-remove-btn">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationPage;
